import React, { type ChangeEvent, type ForwardedRef, useCallback, useEffect, useState } from 'react';

import { detectIsSafari } from '@pkg/utils';

import config from '@app/config';

import { DepAssetError } from './DepAssetError';

import { StyledImage, StyledVideo } from './DepNftAsset.style';

export interface DepAssetHandlerProps {
    imageUrl?: string;
    videoUrl?: string;
    fallback?: string;
    width?: string | number;
    height?: string | number;
    autoPlay?: boolean;
    controls?: boolean;
    isSold?: boolean;
    errorSquareBackground?: boolean;
    // ignoreVideoError would display the poster image if video can't load
    ignoreVideoError?: boolean;
    noErrorText?: boolean;
    onLoad?(): void;
    onError?(): void;
    imageRef?: ForwardedRef<HTMLImageElement>;
    videoRef?: ForwardedRef<HTMLVideoElement>;
    objectFit?: 'contain' | 'cover';
}

// TODO: Avoid defaulting to `true` on `boolean` props.
export function DepAssetHandler({
    imageUrl,
    videoUrl,
    fallback,
    width,
    height,
    autoPlay = true,
    controls = false,
    isSold = false,
    errorSquareBackground = true,
    ignoreVideoError = true,
    onLoad,
    onError,
    imageRef,
    videoRef,
    noErrorText,
    objectFit = 'cover',
}: DepAssetHandlerProps) {
    const [error, setError] = useState(false);
    const [forceImage, setForceImage] = useState(false);
    const [allowFallback, setAllowFallback] = useState(false);
    const [showControls, setShowControls] = useState(false);

    useEffect(() => {
        if (error) setError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fallback]);

    useEffect(() => {
        if (error) {
            onError?.();
        }
    }, [error, onError]);

    const handleSetError = useCallback(() => {
        if (!allowFallback) {
            if (fallback || videoUrl) {
                setAllowFallback(true);
                return;
            }
        }

        setError(true);
        onLoad?.();
    }, [videoUrl, fallback, onLoad, allowFallback]);

    function handleOnLoadedData({ target }: ChangeEvent<HTMLVideoElement>) {
        // Safari removes the poster data on audio files.
        const isSafari = detectIsSafari();
        const ext = (videoUrl?.split('.').pop() || '').toLowerCase();

        if (isSafari && imageUrl && config.audioExtensions.indexOf(ext) > -1) {
            const parent = target.parentElement as HTMLDivElement;
            parent.style.backgroundImage = `url(${imageUrl})`;
        }

        onLoad?.();
    }

    function handleVideoError({ target }: ChangeEvent<HTMLVideoElement>) {
        if (target.error) {
            if (ignoreVideoError && imageUrl) {
                setForceImage(true);
            } else {
                handleSetError();
            }
        }
    }

    function handleImageError(event: ChangeEvent<HTMLImageElement>) {
        event.preventDefault();
        event.stopPropagation();
        handleSetError();
    }

    if (error) {
        return <DepAssetError errorSquareBackground={errorSquareBackground} noErrorText={noErrorText} />;
    }

    const videoFallbackUrl = fallback || imageUrl;

    if (videoUrl && !forceImage) {
        return (
            <StyledVideo
                loop
                muted
                playsInline
                disablePictureInPicture
                ref={videoRef}
                src={allowFallback ? videoFallbackUrl : videoUrl}
                width={width}
                height={height}
                autoPlay={autoPlay}
                controls={showControls}
                poster={imageUrl}
                $isSold={isSold}
                draggable="false"
                preload="auto"
                onLoad={handleOnLoadedData}
                onLoadedData={handleOnLoadedData}
                onError={handleVideoError}
                onCanPlay={() => setShowControls(controls)}
            />
        );
    }

    const imageSrc = allowFallback && fallback ? fallback : imageUrl;

    // TODO: Can we remove the `thumbnail` class?
    return (
        <StyledImage
            ref={imageRef}
            data-src={JSON.stringify({ imageUrl, fallback, imageSrc })}
            src={imageSrc}
            $isSold={isSold}
            $dataSrc={imageSrc}
            className="thumbnail"
            draggable="false"
            onError={handleImageError}
            onLoad={onLoad}
            $objectFit={objectFit}
            loading="lazy"
        />
    );
}
